/**
 * KeyboardShortcutHint Component
 * Displays keyboard shortcut hints for common actions.
 */
import React, { useState, useEffect } from 'react';

interface ShortcutDefinition {
  key: string;
  description: string;
  modifier?: 'shift' | 'ctrl' | 'alt';
}

interface KeyboardShortcutHintProps {
  visible?: boolean;
  className?: string;
}

const shortcuts: ShortcutDefinition[] = [
  { key: 'A', description: 'Accept', modifier: 'shift' },
  { key: 'E', description: 'Edit', modifier: 'shift' },
  { key: 'R', description: 'Reject', modifier: 'shift' },
  { key: 'N', description: 'Next comment', modifier: 'shift' },
  { key: 'P', description: 'Previous comment', modifier: 'shift' },
];

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 border border-gray-300 rounded shadow-sm">
    {children}
  </kbd>
);

const KeyboardShortcutHint: React.FC<KeyboardShortcutHintProps> = ({
  visible = true,
  className = '',
}) => {
  const [showHint, setShowHint] = useState(false);
  const [lastAction, setLastAction] = useState<string | null>(null);

  // Listen for keyboard shortcut activations
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey) return;

      const key = e.key.toUpperCase();
      const shortcut = shortcuts.find(s => s.key === key && s.modifier === 'shift');

      if (shortcut) {
        setLastAction(shortcut.description);
        setShowHint(true);

        // Hide after animation
        setTimeout(() => setShowHint(false), 1000);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Keyboard shortcut feedback popup */}
      {showHint && lastAction && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg animate-fade-in"
          style={{ animationDuration: '0.15s' }}
        >
          <span className="font-medium">{lastAction}</span>
        </div>
      )}

      {/* Shortcuts legend (collapsible) */}
      <ShortcutsLegend className={className} />
    </>
  );
};

interface ShortcutsLegendProps {
  className?: string;
}

const ShortcutsLegend: React.FC<ShortcutsLegendProps> = ({ className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        title="Keyboard shortcuts"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 007.92 12.446A9 9 0 1112 3z"
          />
        </svg>
        <span>Shortcuts</span>
        <svg
          className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs space-y-1.5 animate-fade-in">
          {shortcuts.map((shortcut) => (
            <div key={shortcut.key} className="flex items-center justify-between gap-4">
              <span className="text-gray-600">{shortcut.description}</span>
              <span className="flex items-center gap-1">
                {shortcut.modifier && <Kbd>Shift</Kbd>}
                <span className="text-gray-400">+</span>
                <Kbd>{shortcut.key}</Kbd>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KeyboardShortcutHint;
export { ShortcutsLegend };
